// =============================================================================
// ManHoursReportScreen — /reports/man-hours  (T-369)
// All approved timesheet hours rolled up by project, over a date range, with
// filters for client / carrier / asset (structure) owner. Every worker type is
// counted (field, directors, labour-hire, subbies) — this is the "all hours
// reportable" view used to extract on-site man-hours for a client (e.g. total
// man-hours on Amplitel in June). Excel + CSV export to hand to the client.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Clock, Download, FileSpreadsheet, FileText, Building2, Users, CalendarDays, AlertTriangle, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Button, Select, Spinner, EmptyState } from "@/components/ui";
import { exportCsv, dated } from "@/lib/exportCsv";
import { exportManHoursXlsx, type ManHoursProjectRow, type ManHoursPersonRow } from "./manHoursExport";

function workerType(t: string | null | undefined): string {
  if (t === "director") return "Directors";
  if (t === "subcontractor") return "Subbies";
  if (t === "labour_hire") return "Labour hire";
  return "Field";
}

function monthStartISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
const n2 = (n: number) => Math.round(n * 100) / 100;

interface ProjAgg {
  project_id:   string;
  project_code: string;
  project_name: string;
  site_name:    string;
  client:       string;
  carrier:      string;
  asset_owner:  string;
  total:        number;
  std:          number;
  th:           number;
  dt:           number;
  days:         Set<string>;
  people:       Set<string>;
  byType:       Record<string, number>;
}

export default function ManHoursReportScreen() {
  const user = useRequireAuth();
  const canView = hasRole(user, "manager");

  const [start, setStart] = useState(monthStartISO());
  const [end, setEnd] = useState(todayISO());
  const [clientF, setClientF] = useState("");
  const [carrierF, setCarrierF] = useState("");
  const [ownerF, setOwnerF] = useState("");

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["man_hours_report", start, end],
    enabled: !!user && canView && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select(`
          work_date, total_hours, hours_std, hours_th, hours_dt, employee_id,
          employees:employee_id ( first_name, last_name, employment_type ),
          project:project_id (
            project_id, project_code, project_name,
            client:client_id ( legal_name ),
            carrier:carrier_id ( carrier_code, legal_name ),
            site:site_id ( site_name, asset_owner:asset_owner_id ( display_name ) )
          )
        `)
        .not("approved_at", "is", null)
        .not("project_id", "is", null)
        .gte("work_date", start)
        .lte("work_date", end)
        .limit(10000);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Derive filter options from the loaded rows.
  const opts = useMemo(() => {
    const clients = new Set<string>();
    const carriers = new Set<string>();
    const owners = new Set<string>();
    for (const r of rows) {
      const p = r.project;
      if (!p) continue;
      if (p.client?.legal_name) clients.add(p.client.legal_name);
      if (p.carrier?.carrier_code) carriers.add(p.carrier.carrier_code);
      if (p.site?.asset_owner?.display_name) owners.add(p.site.asset_owner.display_name);
    }
    return {
      clients: Array.from(clients).sort(),
      carriers: Array.from(carriers).sort(),
      owners: Array.from(owners).sort(),
    };
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    const p = r.project;
    if (!p) return false;
    if (clientF && p.client?.legal_name !== clientF) return false;
    if (carrierF && p.carrier?.carrier_code !== carrierF) return false;
    if (ownerF && p.site?.asset_owner?.display_name !== ownerF) return false;
    return true;
  }), [rows, clientF, carrierF, ownerF]);

  const projects = useMemo(() => {
    const map = new Map<string, ProjAgg>();
    for (const r of filtered) {
      const p = r.project;
      const id = p.project_id as string;
      let g = map.get(id);
      if (!g) {
        g = {
          project_id: id,
          project_code: p.project_code ?? "—",
          project_name: p.project_name ?? "",
          site_name: p.site?.site_name ?? "",
          client: p.client?.legal_name ?? "—",
          carrier: p.carrier?.carrier_code ?? "—",
          asset_owner: p.site?.asset_owner?.display_name ?? "—",
          total: 0, std: 0, th: 0, dt: 0,
          days: new Set(), people: new Set(),
          byType: { Field: 0, Directors: 0, "Labour hire": 0, Subbies: 0 },
        };
        map.set(id, g);
      }
      const h = Number(r.total_hours) || 0;
      g.total += h;
      g.std += Number(r.hours_std) || 0;
      g.th += Number(r.hours_th) || 0;
      g.dt += Number(r.hours_dt) || 0;
      g.days.add(r.work_date);
      g.people.add(r.employee_id);
      g.byType[workerType(r.employees?.employment_type)] += h;
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [filtered]);

  // Per-person-per-project detail (for the Excel "By person" sheet + CSV).
  const peopleRows = useMemo<ManHoursPersonRow[]>(() => {
    const map = new Map<string, ManHoursPersonRow & { _days: Set<string> }>();
    for (const r of filtered) {
      const p = r.project;
      const emp = r.employees;
      const name = [emp?.first_name, emp?.last_name].filter(Boolean).join(" ") || "Unnamed";
      const key = `${r.employee_id}|${p.project_id}`;
      let g = map.get(key);
      if (!g) {
        g = {
          name,
          worker_type: workerType(emp?.employment_type),
          project_code: p.project_code ?? "—",
          project_name: p.project_name ?? "",
          client: p.client?.legal_name ?? "—",
          days: 0,
          total_hours: 0,
          _days: new Set(),
        };
        map.set(key, g);
      }
      g.total_hours += Number(r.total_hours) || 0;
      g._days.add(r.work_date);
    }
    return Array.from(map.values())
      .map((g) => ({
        name: g.name, worker_type: g.worker_type, project_code: g.project_code,
        project_name: g.project_name, client: g.client,
        days: g._days.size, total_hours: g.total_hours,
      }))
      .sort((a, b) => a.name.localeCompare(b.name) || b.total_hours - a.total_hours);
  }, [filtered]);

  const totals = useMemo(() => {
    const total = projects.reduce((a, p) => a + p.total, 0);
    const people = new Set<string>();
    const days = new Set<string>();
    for (const r of filtered) { people.add(r.employee_id); days.add(r.work_date); }
    const byType: Record<string, number> = { Field: 0, Directors: 0, "Labour hire": 0, Subbies: 0 };
    for (const p of projects) for (const k of Object.keys(byType)) byType[k] += p.byType[k];
    return { total, projects: projects.length, people: people.size, days: days.size, byType };
  }, [projects, filtered]);

  const filterLine = useMemo(() => {
    const bits: string[] = [];
    if (clientF) bits.push(`Client: ${clientF}`);
    if (carrierF) bits.push(`Carrier: ${carrierF}`);
    if (ownerF) bits.push(`Asset owner: ${ownerF}`);
    return bits.join(" · ");
  }, [clientF, carrierF, ownerF]);

  function toProjectExportRows(): ManHoursProjectRow[] {
    return projects.map((p) => ({
      project_code: p.project_code,
      project_name: p.project_name,
      site_name: p.site_name,
      client: p.client,
      carrier: p.carrier,
      asset_owner: p.asset_owner,
      people: p.people.size,
      days: p.days.size,
      hours_std: n2(p.std),
      hours_th: n2(p.th),
      hours_dt: n2(p.dt),
      total_hours: n2(p.total),
    }));
  }

  if (!user) return null;
  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={AlertTriangle} title="Managers only" description="The man-hours report is available to managers and directors." /></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-20">
      <Link to="/finance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14} /> Finance hub
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Clock size={20} className="text-accent" /> Man-hours by project
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            All approved on-site hours — every worker type — rolled up by project. Filter by client, carrier or asset owner and export for the client.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/reports/hours"><Button variant="secondary" icon={FileText}>Client reports</Button></Link>
          <Button variant="secondary" icon={Download} disabled={projects.length === 0}
            onClick={() => exportCsv(dated("man_hours_by_project"), toProjectExportRows(), [
              { header: "Project", value: (r) => r.project_code },
              { header: "Name", value: (r) => r.project_name },
              { header: "Site", value: (r) => r.site_name },
              { header: "Client", value: (r) => r.client },
              { header: "Carrier", value: (r) => r.carrier },
              { header: "Asset owner", value: (r) => r.asset_owner },
              { header: "People", value: (r) => r.people },
              { header: "On-site days", value: (r) => r.days },
              { header: "Ordinary", value: (r) => r.hours_std },
              { header: "OT 1.5x", value: (r) => r.hours_th },
              { header: "OT 2x", value: (r) => r.hours_dt },
              { header: "Total hrs", value: (r) => r.total_hours },
            ])}>
            CSV
          </Button>
          <Button icon={FileSpreadsheet} disabled={projects.length === 0}
            onClick={() => exportManHoursXlsx(toProjectExportRows(), peopleRows, { start, end, filterLine })}>
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-5">
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider2 text-text-muted">From</span>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink-card text-text font-mono text-[13px]" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider2 text-text-muted">To</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink-card text-text font-mono text-[13px]" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Client</span>
            <Select value={clientF} onChange={(e) => setClientF(e.target.value)} className="mt-1">
              <option value="">All clients</option>
              {opts.clients.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Carrier</span>
            <Select value={carrierF} onChange={(e) => setCarrierF(e.target.value)} className="mt-1">
              <option value="">All carriers</option>
              {opts.carriers.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Asset owner</span>
            <Select value={ownerF} onChange={(e) => setOwnerF(e.target.value)} className="mt-1">
              <option value="">All owners</option>
              {opts.owners.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </label>
        </div>
        {(clientF || carrierF || ownerF || start !== monthStartISO() || end !== todayISO()) && (
          <div className="px-4 pb-4 -mt-1 flex justify-end">
            <Button variant="secondary" size="sm" icon={X}
              onClick={() => { setClientF(""); setCarrierF(""); setOwnerF(""); setStart(monthStartISO()); setEnd(todayISO()); }}>
              Clear filters
            </Button>
          </div>
        )}
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Total man-hours" value={n2(totals.total).toLocaleString()} icon={Clock} accent />
        <KPI label="Projects" value={totals.projects} icon={Building2} />
        <KPI label="People" value={totals.people} icon={Users} />
        <KPI label="On-site days" value={totals.days} icon={CalendarDays} />
      </div>

      {/* Worker-type split */}
      {totals.total > 0 && (
        <div className="mb-5 flex flex-wrap gap-2 text-[12px]">
          {Object.entries(totals.byType).filter(([, v]) => v > 0).map(([k, v]) => (
            <span key={k} className="px-2.5 py-1 rounded-full border border-line bg-ink-raised text-text-muted">
              {k}: <span className="text-text font-mono">{n2(v)}h</span>
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
          <AlertTriangle size={12} className="inline mr-1.5" />{(error as any).message}
        </div>
      )}

      {isLoading ? (
        <div className="py-16 flex justify-center"><Spinner size={20} /></div>
      ) : projects.length === 0 ? (
        <Card><EmptyState icon={Clock} title="No hours in this range" description="No approved timesheet hours match the selected dates and filters." /></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {["Project", "Site", "Client", "Carrier", "Asset owner", "People", "Days", "Total hrs"].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted ${i >= 5 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.project_id} className="border-b border-line hover:bg-ink-hover">
                    <td className="px-4 py-3">
                      <div className="font-mono text-[11px] text-accent">{p.project_code}</div>
                      <div className="text-[12px] text-text-muted truncate max-w-[220px]">{p.project_name}</div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-text-muted">{p.site_name || "—"}</td>
                    <td className="px-4 py-3">{p.client}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-text-muted">{p.carrier}</td>
                    <td className="px-4 py-3 text-[12px]">{p.asset_owner}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.people.size}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.days.size}</td>
                    <td className="px-4 py-3 text-right font-mono text-text font-semibold">{n2(p.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line">
                  <td className="px-4 py-3 text-[12px] font-semibold" colSpan={5}>Total</td>
                  <td className="px-4 py-3 text-right font-mono">{totals.people}</td>
                  <td className="px-4 py-3 text-right font-mono">{totals.days}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-accent">{n2(totals.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
